import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Heart, Loader2, Download, Trash2, Eye, Paintbrush, Share2, Info, MoreHorizontal, CheckCircle2, User, Wallet, ChevronUp, Hash, Pencil, X } from 'lucide-react';
import { CreatorLabel } from '@/components/CreatorLabel';
import { CreatorAvatar } from '@/components/CreatorAvatar';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';
import { toast } from 'sonner';
import { useAuth } from '@pooflabs/web';
import {
  WallpapersResponse,
  setWallpapersViews,
  setWallpapers,
} from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { SkrTokenIcon } from '@/components/SkrTokenIcon';
import { useScreenshotProtection } from '@/hooks/useScreenshotProtection';
import { useWallpaperDownload } from '@/hooks/use-wallpaper-download';
import { haptic } from '@/utils/haptic';

const BASE_WALLPAPER_CATEGORIES = ['Nature', 'Abstract', 'Minimal', 'Dark', 'Gradient', 'Space', 'Urban', 'Cyberpunk'];

// Session-level deduplication: tracks wallpaper view events that have already
// been recorded this session so we never call setWallpapersViews twice for the
// same wallpaper+user pair. Persisted to sessionStorage so it survives page
// reloads — the policy has update: "false" (immutable), so a second attempt
// always returns 403 (logged internally, never thrown). Persisting the guard
// prevents the redundant write attempt on reload.
const VIEWS_SESSION_STORAGE_KEY = 'ss-recorded-views';
function getRecordedViewsSet(): Set<string> {
  try {
    const raw = sessionStorage.getItem(VIEWS_SESSION_STORAGE_KEY);
    return raw ? new Set<string>(JSON.parse(raw) as string[]) : new Set<string>();
  } catch {
    return new Set<string>();
  }
}
function addRecordedView(key: string): void {
  try {
    const set = getRecordedViewsSet();
    set.add(key);
    sessionStorage.setItem(VIEWS_SESSION_STORAGE_KEY, JSON.stringify([...set]));
  } catch {}
}
function hasRecordedView(key: string): boolean {
  return getRecordedViewsSet().has(key);
}


function getFullImageUrl(wallpaper: { imageUrl: string }, _purchased: boolean): string {
  const baseUrl = wallpaper.imageUrl;
  const stripped = baseUrl.split('?')[0];
  const isUnsplash = stripped.includes('unsplash.com') || stripped.includes('images.unsplash');
  // Always serve max quality in full view
  return isUnsplash ? `${stripped}?w=2160&q=90&auto=format` : baseUrl;
}

function isLiveWallpaper(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'live-wallpaper') return true;
  if (stored && stored !== 'live-wallpaper') return false;
  // Legacy fallback: detect from URL extension
  const url = (wallpaper.imageUrl || '').toLowerCase().split('?')[0];
  return /\.(mp4|webm|mov|m4v)$/.test(url);
}

function isRingtone(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  return stored === 'ringtone';
}

function formatAudioTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface WallpaperViewerProps {
  wallpaper: WallpapersResponse;
  onClose: () => void;
  isPurchased: boolean;
  isFavorited: boolean;
  isTrashed?: boolean;
  onPurchase: (wallpaper: WallpapersResponse) => Promise<void>;
  onToggleFavorite: (wallpaper: WallpapersResponse) => Promise<void>;
  onTrash?: (wallpaper: WallpapersResponse) => Promise<void>;
  purchasing: boolean;
  skrBalance: number | null;
  isAdmin?: boolean;
  onAdminDelete?: (wallpaper: WallpapersResponse) => Promise<void>;
  onAdminEditCategory?: (wallpaper: WallpapersResponse, newCategory: string) => Promise<void>;
  availableCategories?: string[];
  wallpapersList?: WallpapersResponse[];
  onNavigate?: (wallpaper: WallpapersResponse) => void;
  /**
   * When true, suppress the enter/exit fade animations so a cross-viewer
   * handoff (ringtone ↔ wallpaper) appears instantaneous with no background flash.
   */
  instantHandoff?: boolean;
}

export const WallpaperViewer: React.FC<WallpaperViewerProps> = ({
  wallpaper,
  onClose,
  isPurchased,
  isFavorited,
  isTrashed = false,
  onPurchase,
  onToggleFavorite,
  onTrash,
  purchasing,
  skrBalance: _skrBalance,
  isAdmin = false,
  onAdminDelete,
  onAdminEditCategory,
  availableCategories,
  wallpapersList,
  onNavigate,
  instantHandoff = false,
}) => {
  const categoryList = availableCategories && availableCategories.length > 0
    ? availableCategories
    : BASE_WALLPAPER_CATEGORIES;
  const { user, login } = useAuth();
  const [showUI, setShowUI] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);

  // NOTE: The floating mini-player pause+hide behaviour when this viewer opens is
  // handled centrally by the parent (HomePage) useEffect that watches selectedWallpaper.
  // Do NOT call stop/pause from here — that would destroy the player state.

  // Record a view when an authenticated user opens the viewer (fire-and-forget).
  // Deduplicated per session: the policy has update: "false" so a second write
  // for the same viewer returns 403. Without deduplication that 403 triggers a
  // state change → re-render → another call → 403 again → infinite flicker loop.
  useEffect(() => {
    if (!user?.address || !wallpaper.id) return;

    // Compose the same key used as the view document ID (wallpaperId + viewerId)
    const sessionKey = `${wallpaper.id}:${user.address}`;
    if (hasRecordedView(sessionKey)) return;

    // Mark as recorded before the async call so a rapid double-render
    // cannot race through the guard and fire two concurrent writes.
    // Persisted to sessionStorage so page reloads don't re-attempt the write.
    addRecordedView(sessionKey);

    setWallpapersViews(wallpaper.id, user.address, {
      viewer: Address.publicKey(user.address),
      viewedAt: Time.Now,
    });
  }, [wallpaper.id, user?.address]);
  const [heartBounce, setHeartBounce] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [adminDeleting, setAdminDeleting] = useState(false);
  const [adminUpdatingCat, setAdminUpdatingCat] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  // Purchase celebration animation — SKR tokens fan out from the Buy button on successful purchase
  const buyButtonRef = useRef<HTMLButtonElement>(null);
  const lastBuyOriginRef = useRef<{ x: number; y: number } | null>(null);
  const wasPurchasedRef = useRef(isPurchased);
  const [purchaseBurst, setPurchaseBurst] = useState<{ id: number; x: number; y: number } | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // ── Image Details panel (swipe-up) ──────────────────────────────────────
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);

  // Drag state for the swipe-up gesture on the image area
  const detailsDragStartY = useRef<number | null>(null);
  const detailsDragStartX = useRef<number | null>(null);
  const detailsDragging = useRef(false);

  // Format a Unix-seconds timestamp as "Tuesday, May 5, 2026 · 18:28"
  const formatDetailDate = (ts: number): string => {
    const d = new Date(ts * 1000);
    const datePart = d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${datePart} · ${hh}:${mm}`;
  };

  // Derive a display filename from the wallpaper record
  const detailsFilename = (wallpaper as WallpapersResponse & { originalFilename?: string; fileName?: string }).originalFilename
    ?? (wallpaper as WallpapersResponse & { originalFilename?: string; fileName?: string }).fileName
    ?? wallpaper.title
    ?? wallpaper.id;

  // Megapixels (only when dimensions are known)
  const detailsMegapixels = imageDimensions
    ? Math.round((imageDimensions.w * imageDimensions.h) / 1_000_000)
    : null;

  // File size — read from the canonical sizeBytes field (optional, set at upload time)
  const detailsFileSize = wallpaper.sizeBytes;
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Edit dialog state — only for the uploader
  const isUploader = !!user?.address && !!wallpaper.uploadedBy && user.address === wallpaper.uploadedBy;
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState<number>(1);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = () => {
    setEditTitle(wallpaper.title ?? '');
    setEditPrice(wallpaper.price ? wallpaper.price / 1_000_000 : 1);
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { toast.error('Title is required'); return; }
    if (!editPrice || editPrice < 1) { toast.error('Price must be at least 1 SKR'); return; }
    setEditSaving(true);
    try {
      const ok = await setWallpapers(wallpaper.id, {
        title: editTitle.trim(),
        imageUrl: wallpaper.imageUrl,
        uploadedBy: Address.publicKey(wallpaper.uploadedBy),
        createdAt: wallpaper.createdAt,
        downloadCount: wallpaper.downloadCount,
        price: Math.round(editPrice * 1_000_000),
        status: wallpaper.status,
      });
      if (ok) {
        setEditOpen(false);
      } else {
        toast.error('Save failed — permission denied');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setEditSaving(false);
    }
  };

  // Tags (only if present)
  const detailsTags: string[] = (wallpaper as WallpapersResponse & { tags?: string[] }).tags ?? [];

  // Touch/mouse handlers for the image area (swipe-up to open, swipe-down to close)
  const handleImagePointerDown = (clientX: number, clientY: number) => {
    detailsDragStartY.current = clientY;
    detailsDragStartX.current = clientX;
    detailsDragging.current = false;
  };

  const handleImagePointerUp = (clientX: number, clientY: number) => {
    if (detailsDragStartY.current === null) return;
    const deltaY = clientY - detailsDragStartY.current;
    const deltaX = clientX - (detailsDragStartX.current ?? clientX);
    detailsDragStartY.current = null;
    detailsDragStartX.current = null;

    // Only act on predominantly-vertical drags
    if (Math.abs(deltaX) > Math.abs(deltaY) * 0.8) return;

    if (!detailsPanelOpen && deltaY < -50) {
      setDetailsPanelOpen(true);
    } else if (detailsPanelOpen && deltaY > 50) {
      setDetailsPanelOpen(false);
    }
  };
  // ── end Image Details panel ──────────────────────────────────────────────

  // Close More menu on outside click
  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMoreMenu]);
  // Pre-compute all random coin params once per burst — prevents Math.random() re-running on every render
  const burstCoins = useMemo(() => {
    if (!purchaseBurst) return [];
    const total = 14;
    return Array.from({ length: total }).map((_, i) => {
      const baseAngle = -180 + (i * 180) / (total - 1);
      const jitter = (Math.random() - 0.5) * 18;
      const angleDeg = baseAngle + jitter;
      const angle = (angleDeg * Math.PI) / 180;
      const distance = 160 + Math.random() * 100;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 120; // increased upward bias
      const delay = i * 0.025;
      const size = 22 + Math.random() * 10;
      const rotate = 360 + Math.random() * 180;
      return { dx, dy, delay, size, rotate };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseBurst?.id]);
  // Screenshot protection — only active for purchased wallpapers, PrintScreen/Mac combos only
  const { isProtected: showProtectionOverlay } = useScreenshotProtection(isPurchased);

  // Audio player state (for ringtones)
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // Reset audio player when wallpaper changes
  useEffect(() => {
    setAudioPlaying(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
  }, [wallpaper.id]);

  // Touch swipe state
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);

  const currentIndex = wallpapersList ? wallpapersList.findIndex(w => w.id === wallpaper.id) : -1;
  const hasList = !!wallpapersList && wallpapersList.length > 1 && !!onNavigate;

  const navigateNext = useCallback(() => {
    if (!hasList || currentIndex === -1) return;
    haptic.light();
    const nextIndex = (currentIndex + 1) % wallpapersList!.length;
    setSlideDirection('left');
    setImageLoaded(false);
    onNavigate!(wallpapersList![nextIndex]);
  }, [hasList, currentIndex, wallpapersList, onNavigate]);

  const navigatePrev = useCallback(() => {
    if (!hasList || currentIndex === -1) return;
    haptic.light();
    const prevIndex = (currentIndex - 1 + wallpapersList!.length) % wallpapersList!.length;
    setSlideDirection('right');
    setImageLoaded(false);
    onNavigate!(wallpapersList![prevIndex]);
  }, [hasList, currentIndex, wallpapersList, onNavigate]);

  // Load real dimensions — uses Image for static files, HTMLVideoElement for live wallpapers
  useEffect(() => {
    setImageDimensions(null);
    const src = getFullImageUrl(wallpaper, true);
    if (isLiveWallpaper(wallpaper)) {
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        if (vid.videoWidth && vid.videoHeight) {
          setImageDimensions({ w: vid.videoWidth, h: vid.videoHeight });
        }
        vid.src = '';
      };
      vid.src = src;
    } else {
      const img = new Image();
      img.onload = () => setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = src;
    }
  }, [wallpaper.id, wallpaper.imageUrl]);

  // Reset slide direction after animation
  useEffect(() => {
    if (slideDirection) {
      const t = setTimeout(() => setSlideDirection(null), 350);
      return () => clearTimeout(t);
    }
  }, [slideDirection]);

  // Reset stale refs when the viewed wallpaper changes so the burst animation
  // does not falsely fire when navigating from an unowned card to an already-owned card.
  useEffect(() => {
    wasPurchasedRef.current = isPurchased;
    lastBuyOriginRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallpaper.id]);

  // Trigger SKR burst animation when a purchase completes (isPurchased flips false → true)
  useEffect(() => {
    if (!wasPurchasedRef.current && isPurchased) {
      haptic.success();
      const origin = lastBuyOriginRef.current;
      if (origin) {
        setPurchaseBurst({ id: Date.now(), x: origin.x, y: origin.y });
        const t = setTimeout(() => setPurchaseBurst(null), 5400);
        wasPurchasedRef.current = isPurchased;
        return () => clearTimeout(t);
      }
    }
    wasPurchasedRef.current = isPurchased;
  }, [isPurchased]);

  const handleToggleFavorite = async () => {
    haptic.light();
    setHeartBounce(true);
    setTimeout(() => setHeartBounce(false), 600);
    await onToggleFavorite(wallpaper);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowRight') {
      navigateNext();
    } else if (e.key === 'ArrowLeft') {
      navigatePrev();
    }
  }, [onClose, navigateNext, navigatePrev]);

  useBodyScrollLock();

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);


  const handleAdminDelete = async () => {
    if (!onAdminDelete) return;
    setAdminDeleting(true);
    try {
      await onAdminDelete(wallpaper);
    } finally {
      setAdminDeleting(false);
    }
  };

  const handleAdminSetCategory = async (cat: string) => {
    if (!onAdminEditCategory) return;
    setAdminUpdatingCat(true);
    try {
      const newCategory = cat === wallpaper.category ? '' : cat;
      await onAdminEditCategory(wallpaper, newCategory);
      setShowCategoryPicker(false);
    } finally {
      setAdminUpdatingCat(false);
    }
  };

  const { downloadWallpaper } = useWallpaperDownload();
  const handleDownload = () => downloadWallpaper(wallpaper);

  const getBuyButtonState = () => {
    const wallpaperPriceSKR = wallpaper.price ? wallpaper.price / 1000000 : 1;
    if (!user) return { label: 'Connect Wallet', disabled: false, variant: 'connect' };
    if (isPurchased) return { label: 'Purchased', disabled: true, variant: 'success' };
    if (purchasing) return { label: 'Purchasing...', disabled: true, variant: 'loading' };
    return { label: `Buy for ${wallpaperPriceSKR} SKR`, disabled: false, variant: 'primary' };
  };

  const buyState = getBuyButtonState();

  return createPortal(
    <motion.div
      initial={instantHandoff ? { opacity: 1 } : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={instantHandoff ? { opacity: 1 } : { opacity: 0 }}
      transition={instantHandoff ? { duration: 0 } : { duration: 0.2 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10002,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Ringtone background panel */}
      {isRingtone(wallpaper) && (
        <div
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(160deg, #1a0800 0%, #2d1400 35%, #0d0500 65%, #1a0800 100%)',
          }}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
            isSwiping.current = false;
          }}
          onTouchEnd={(e) => {
            if (!hasList) return;
            const deltaX = e.changedTouches[0].clientX - touchStartX.current;
            const deltaY = e.changedTouches[0].clientY - touchStartY.current;
            if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
              isSwiping.current = true;
              if (deltaX < 0) navigateNext();
              else navigatePrev();
            }
          }}
        >
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 80% 60% at 50% 35%, rgba(167,139,250,0.14) 0%, transparent 65%)',
            animation: 'ringtoneViewerGlow 3.5s ease-in-out infinite alternate',
          }} />
          {/* Decorative waveform — large, behind everything */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, opacity: 0.18,
          }}>
            {[0.35, 0.55, 0.8, 0.6, 1.0, 0.45, 0.75, 0.5, 0.9, 0.65, 0.4, 0.7, 0.55, 0.85, 0.35, 0.6, 0.95, 0.5, 0.75, 0.4].map((h, i) => (
              <div key={i} style={{
                width: 5, borderRadius: 3,
                background: '#a78bfa',
                height: `${h * 120}px`,
                animation: `waveBarV${(i % 4) + 1} ${0.7 + (i % 3) * 0.25}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.06}s`,
              }} />
            ))}
          </div>
        </div>
      )}

      {/* Full-screen image (non-ringtone only) */}
      {!isRingtone(wallpaper) && (
      <div
        style={{
          position: 'absolute', inset: 0,
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
        } as React.CSSProperties}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => handleImagePointerDown(e.clientX, e.clientY)}
        onMouseUp={(e) => handleImagePointerUp(e.clientX, e.clientY)}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
          touchStartY.current = e.touches[0].clientY;
          isSwiping.current = false;
          handleImagePointerDown(e.touches[0].clientX, e.touches[0].clientY);
        }}
        onTouchEnd={(e) => {
          const touch = e.changedTouches[0];
          handleImagePointerUp(touch.clientX, touch.clientY);
          if (!hasList) return;
          const deltaX = touch.clientX - touchStartX.current;
          const deltaY = touch.clientY - touchStartY.current;
          // Horizontal swipe: deltaX > 50px and more horizontal than vertical
          if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
            isSwiping.current = true;
            if (deltaX < 0) {
              // Swipe left → next
              navigateNext();
            } else {
              // Swipe right → previous
              navigatePrev();
            }
          }
        }}
        onClick={(e) => {
          // Tapping the image closes the details panel if open
          if (detailsPanelOpen) {
            e.stopPropagation();
            setDetailsPanelOpen(false);
          }
        }}
      >
        {!imageLoaded && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg, #141420 0%, #0a0a0f 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}>
            <Loader2 size={32} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        <AnimatePresence mode="wait" initial={false}>
          {isLiveWallpaper(wallpaper) ? (
            isPurchased ? (
              /* Live wallpaper, purchased: video with slide animation */
              <motion.video
                key={wallpaper.id}
                src={getFullImageUrl(wallpaper, true)}
                autoPlay
                loop
                muted
                playsInline
                onLoadedData={() => setImageLoaded(true)}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                initial={{
                  opacity: 0,
                  x: slideDirection === 'left' ? 60 : slideDirection === 'right' ? -60 : 0,
                }}
                animate={{ opacity: imageLoaded ? 1 : 0, x: 0 }}
                exit={{
                  opacity: 0,
                  x: slideDirection === 'left' ? -60 : slideDirection === 'right' ? 60 : 0,
                }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  pointerEvents: 'none',
                } as React.CSSProperties}
              />
            ) : (
              /* Live wallpaper, unpurchased: full-quality video + protection overlay */
              <>
                <motion.video
                  key={wallpaper.id}
                  src={getFullImageUrl(wallpaper, true)}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onLoadedData={() => setImageLoaded(true)}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  initial={{
                    opacity: 0,
                    x: slideDirection === 'left' ? 60 : slideDirection === 'right' ? -60 : 0,
                  }}
                  animate={{ opacity: imageLoaded ? 1 : 0, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: slideDirection === 'left' ? -60 : slideDirection === 'right' ? 60 : 0,
                  }}
                  transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  } as React.CSSProperties}
                />
                {/* Transparent overlay — intercepts long-press/touch on unpurchased video,
                    sits above the video but below the UI panels (zIndex 10) */}
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    zIndex: 2,
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  } as React.CSSProperties}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </>
            )
          ) : isPurchased ? (
            /* Static image, purchased: <img> tag for quality */
            <motion.img
              key={wallpaper.id}
              src={getFullImageUrl(wallpaper, true)}
              alt={wallpaper.title}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              className="protected-image"
              initial={{
                opacity: 0,
                x: slideDirection === 'left' ? 60 : slideDirection === 'right' ? -60 : 0,
              }}
              animate={{ opacity: imageLoaded ? 1 : 0, x: 0 }}
              exit={{
                opacity: 0,
                x: slideDirection === 'left' ? -60 : slideDirection === 'right' ? 60 : 0,
              }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                WebkitUserDrag: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                pointerEvents: 'none',
              } as React.CSSProperties}
            />
          ) : (
            /* Static image, unpurchased: CSS background-image div — no <img> = no "Save Image" context menu.
               Full quality, no blur. */
            <>
              <motion.div
                key={wallpaper.id}
                initial={{
                  opacity: 0,
                  x: slideDirection === 'left' ? 60 : slideDirection === 'right' ? -60 : 0,
                }}
                animate={{ opacity: imageLoaded ? 1 : 0, x: 0 }}
                exit={{
                  opacity: 0,
                  x: slideDirection === 'left' ? -60 : slideDirection === 'right' ? 60 : 0,
                }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: `url(${getFullImageUrl(wallpaper, true)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  pointerEvents: 'none',
                }}
                ref={(el) => {
                  if (el && !imageLoaded) {
                    const img = new Image();
                    img.onload = () => setImageLoaded(true);
                    img.src = getFullImageUrl(wallpaper, true);
                  }
                }}
              />
              {/* Transparent overlay — intercepts long-press/touch on unpurchased images,
                  sits above the bg div but below the UI panels (zIndex 10) */}
              <div
                style={{
                  position: 'absolute', inset: 0,
                  zIndex: 2,
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                } as React.CSSProperties}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />
            </>
          )}
        </AnimatePresence>
        {/* Gradient overlay at bottom */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.3) 40%, transparent 70%)',
          pointerEvents: 'none',
        }} />

      </div>
      )} {/* end !isRingtone image section */}

      {/* UI overlay */}
      <AnimatePresence>
        {showUI && (
          <>
            {/* Top bar — back (left) + favorite (right) */}
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '20px 20px 0',
                zIndex: 10,
              }}
              onClick={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onTouchEnd={e => e.stopPropagation()}
            >
              {/* Back / close button */}
              <motion.button
                whileHover={{ scale: 1.08, background: 'rgba(255,255,255,0.18)' }}
                whileTap={{ scale: 0.92 }}
                onClick={() => { haptic.light(); onClose(); }}
                style={{
                  width: 44, height: 44,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(14px)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff',
                }}
              >
                <ArrowLeft size={18} />
              </motion.button>

              {/* Favorite / heart button */}
              <motion.button
                animate={heartBounce ? { scale: [1, 1.25, 0.9, 1.1, 1] } : { scale: 1 }}
                transition={{ duration: 0.4 }}
                whileHover={{ scale: 1.08, background: isFavorited ? 'hsla(262, 83%, 66%, 0.3)' : 'rgba(255,255,255,0.18)' }}
                whileTap={{ scale: 0.92 }}
                onClick={user ? handleToggleFavorite : undefined}
                style={{
                  width: 44, height: 44,
                  borderRadius: '50%',
                  background: isFavorited ? 'hsla(262, 83%, 66%, 0.22)' : 'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(14px)',
                  border: isFavorited ? '1px solid hsla(262, 83%, 66%, 0.55)' : '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: user ? 'pointer' : 'default',
                  color: isFavorited ? 'hsl(262, 83%, 66%)' : '#fff',
                  opacity: user ? 1 : 0.4,
                }}
              >
                <Heart
                  size={18}
                  fill={isFavorited ? 'hsl(262, 83%, 66%)' : 'none'}
                />
              </motion.button>
            </motion.div>

            {/* Bottom panel */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                padding: '0 14px max(8px, env(safe-area-inset-bottom, 8px))',
                zIndex: 10,
              }}
              onClick={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onTouchEnd={e => e.stopPropagation()}
            >
              {/* Ringtone audio player (above the main card when applicable) */}
              {isRingtone(wallpaper) && (
                <div style={{ marginBottom: 12 }}>
                  <audio
                    ref={audioRef}
                    src={wallpaper.imageUrl || ''}
                    preload="metadata"
                    onTimeUpdate={() => setAudioCurrentTime(audioRef.current?.currentTime ?? 0)}
                    onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration ?? 0)}
                    onEnded={() => setAudioPlaying(false)}
                  />
                  <MiniPlayerBar
                    title={wallpaper.title ?? 'Untitled'}
                    subtitle={
                      (wallpaper as WallpapersResponse & { category?: string }).category
                        ? `${(wallpaper as WallpapersResponse & { category?: string }).category} • ${formatAudioTime(audioDuration)}`
                        : formatAudioTime(audioDuration)
                    }
                    isPlaying={audioPlaying}
                    progress={audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}
                    onPlayPause={(e) => {
                      e.stopPropagation();
                      if (!audioRef.current) return;
                      if (audioPlaying) {
                        audioRef.current.pause();
                        setAudioPlaying(false);
                      } else {
                        audioRef.current.play().then(() => setAudioPlaying(true)).catch(() => {});
                      }
                    }}
                    showNextButton={false}
                    showDismissButton={false}
                  />
                </div>
              )}

              {/* ─── Main detail card ─── */}
              <div style={{
                borderRadius: 24,
                background: 'rgba(7,8,15,0.4)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                padding: '20px 20px 20px',
                display: 'flex', flexDirection: 'column', gap: 14,
              }}>

                {/* Row 1: Title + resolution badge */}
                {!isRingtone(wallpaper) && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <h2 style={{
                      fontSize: 22, fontWeight: 700, color: '#fff',
                      margin: 0, lineHeight: 1.2,
                      fontFamily: "'Syne', sans-serif",
                      flex: 1,
                    }}>
                      {wallpaper.title}
                    </h2>
                    {/* Right column: resolution badge only */}
                    {imageDimensions ? (
                      <span style={{
                        flexShrink: 0,
                        padding: '4px 10px',
                        borderRadius: 20,
                        background: 'rgba(45,212,191,0.1)',
                        border: '1px solid rgba(45,212,191,0.35)',
                        color: '#2dd4bf',
                        fontSize: 11,
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 500,
                        letterSpacing: '0.03em',
                        whiteSpace: 'nowrap',
                        marginTop: 3,
                      }}>
                        {imageDimensions.w}×{imageDimensions.h}
                      </span>
                    ) : null}
                  </div>
                )}

                {/* Row 2: Category (left) + chevron toggle (right) */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Paintbrush size={13} style={{ color: 'rgba(255,255,255,0.38)', flexShrink: 0 }} />
                    {wallpaper.category ? (
                      <span style={{
                        fontSize: 13, color: 'rgba(255,255,255,0.6)',
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 500,
                      }}>
                        {wallpaper.category}
                      </span>
                    ) : (
                      <span style={{
                        fontSize: 13, color: 'rgba(255,255,255,0.3)',
                        fontFamily: "'Syne', sans-serif",
                        fontStyle: 'italic',
                      }}>
                        Uncategorized
                      </span>
                    )}
                  </div>
                  {!isRingtone(wallpaper) && (
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 4,
                        cursor: 'pointer',
                        color: 'rgba(255,255,255,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                      onClick={() => setDetailsPanelOpen(v => !v)}
                      title="Show image details"
                    >
                      <ChevronUp
                        size={18}
                        strokeWidth={2}
                        style={{
                          transition: 'transform 0.25s ease',
                          transform: detailsPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>
                  )}
                </div>

                {/* Row 2b: Creator */}
                {wallpaper.uploadedBy && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CreatorAvatar walletAddress={wallpaper.uploadedBy} size={22} />
                    <CreatorLabel
                      walletAddress={wallpaper.uploadedBy}
                      style={{ fontSize: 13 }}
                    />
                  </div>
                )}

                {/* Divider */}
                <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '0 -4px' }} />

                {/* Row 3: Primary action + (when purchased) Download + Trash icons */}
                <motion.div
                  layout
                  transition={{ layout: { duration: 0.35, ease: [0.4, 0, 0.2, 1] } }}
                  style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    {isPurchased ? (
                      <motion.div
                        key="owned"
                        layout
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        style={{
                          flex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                          padding: '13px 20px',
                          borderRadius: 50,
                          background: 'linear-gradient(135deg, rgba(34,197,94,0.95) 0%, rgba(21,128,61,0.95) 100%)',
                          border: '1px solid rgba(74,222,128,0.55)',
                          color: '#fff',
                          cursor: 'default', fontSize: 15, fontWeight: 700,
                          fontFamily: "'Syne', sans-serif",
                          boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <CheckCircle2 size={15} strokeWidth={2.2} />
                        Owned
                      </motion.div>
                    ) : (
                      <motion.button
                        key="buy"
                        layout
                        ref={buyButtonRef}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        whileHover={buyState.disabled ? {} : { scale: 1.02, boxShadow: '0 0 28px rgba(139,92,246,0.55)' }}
                        whileTap={buyState.disabled ? {} : { scale: 0.97 }}
                        onClick={() => {
                          if (buyState.disabled) return;
                          if (!user) {
                            // Guest: trigger wallet connection
                            window.dispatchEvent(new Event('poof:close-overlays'));
                            setTimeout(async () => { try { await login(); } catch {} }, 0);
                            return;
                          }
                          haptic.medium();
                          const rect = buyButtonRef.current?.getBoundingClientRect();
                          if (rect) {
                            lastBuyOriginRef.current = {
                              x: rect.left + rect.width / 2,
                              y: rect.top + rect.height / 2,
                            };
                          }
                          onPurchase(wallpaper);
                        }}
                        disabled={buyState.disabled}
                        style={{
                          flex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                          padding: '13px 20px',
                          borderRadius: 50,
                          background: buyState.variant === 'warning'
                            ? 'rgba(245,158,11,0.18)'
                            : buyState.variant === 'connect'
                            ? 'rgba(255,255,255,0.1)'
                            : 'linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(109,40,217,0.9) 100%)',
                          border: buyState.variant === 'warning'
                            ? '1px solid rgba(245,158,11,0.4)'
                            : buyState.variant === 'connect'
                            ? '1px solid rgba(255,255,255,0.2)'
                            : '1px solid rgba(167,139,250,0.5)',
                          color: '#fff',
                          cursor: buyState.disabled ? 'not-allowed' : 'pointer',
                          opacity: buyState.disabled ? 0.68 : 1,
                          fontSize: 15, fontWeight: 700,
                          fontFamily: "'Syne', sans-serif",
                          boxShadow: (!buyState.disabled && buyState.variant === 'primary')
                            ? '0 4px 20px rgba(139,92,246,0.3)'
                            : 'none',
                        }}
                      >
                        {purchasing ? (
                          <Loader2 size={17} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : buyState.variant === 'connect' ? (
                          <Wallet size={17} strokeWidth={2.2} />
                        ) : (
                          <SkrTokenIcon size={17} />
                        )}
                        {buyState.label}
                      </motion.button>
                    )}

                    {/* Download icon — appears when purchased, or always for admin (server enforces the bypass) */}
                    {(isPurchased || isAdmin) && (
                      <motion.button
                        key="download"
                        layout
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ duration: 0.3, delay: 0.08, ease: [0.4, 0, 0.2, 1] }}
                        whileHover={{ scale: 1.08, background: 'rgba(255,255,255,0.12)' }}
                        whileTap={{ scale: 0.92 }}
                        onClick={handleDownload}
                        style={{
                          flexShrink: 0,
                          width: 44, height: 44,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.14)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'rgba(255,255,255,0.9)',
                        }}
                      >
                        <Download size={17} />
                      </motion.button>
                    )}

                    {/* Trash icon — appears only when purchased and trash handler is available */}
                    {isPurchased && !!user && !!onTrash && !isTrashed && (
                      <motion.button
                        key="trash"
                        layout
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ duration: 0.3, delay: 0.14, ease: [0.4, 0, 0.2, 1] }}
                        whileHover={{ scale: 1.08, background: 'rgba(239,68,68,0.18)', borderColor: 'rgba(239,68,68,0.4)' }}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => { haptic.medium(); onTrash(wallpaper); }}
                        style={{
                          flexShrink: 0,
                          width: 44, height: 44,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.14)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'rgba(255,255,255,0.9)',
                        }}
                      >
                        <Trash2 size={17} />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </motion.div>

              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Swipe-up Image Details Panel ─────────────────────────────────── */}

      <AnimatePresence>
        {detailsPanelOpen && (
          <motion.div
            key="details-panel"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320, mass: 0.85 }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              background: 'rgba(8,8,12,0.97)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
              borderTop: '1px solid rgba(255,255,255,0.09)',
              borderRadius: '24px 24px 0 0',
              paddingBottom: 'max(28px, env(safe-area-inset-bottom, 28px))',
              fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.4 }}
            onDragEnd={(_e, info) => {
              if (info.offset.y > 80) setDetailsPanelOpen(false);
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: 10,
              paddingBottom: 4,
            }}>
              <div style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.22)',
              }} />
            </div>

            <div style={{ padding: '10px 24px 0' }}>
              {/* Top row: date */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#ffffff',
                    fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                    letterSpacing: '0.01em',
                    lineHeight: 1.3,
                  }}>
                    {formatDetailDate(wallpaper.createdAt)}
                  </div>
                  <div style={{
                    marginTop: 5,
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.45)',
                    fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                    letterSpacing: '0.01em',
                    wordBreak: 'break-all',
                  }}>
                    {detailsFilename}
                  </div>
                  {/* Category as path-style line */}
                  {wallpaper.category && (
                    <div style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.28)',
                      fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                      letterSpacing: '0.01em',
                    }}>
                      /Internal storage/{wallpaper.category}
                    </div>
                  )}
                </div>
              </div>

              {/* Generous spacer */}
              <div style={{ height: 28 }} />

              {/* Section header row: label only */}
              <div style={{
                marginBottom: 12,
              }}>
                <div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#ffffff',
                  fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                  letterSpacing: '0.01em',
                }}>
                  Image details
                </div>
              </div>

              {/* Metadata inline row: "1080×2400 | 3MP | 230 KB" */}
              {(imageDimensions || detailsFileSize) && (
                <div style={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.45)',
                  fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                  letterSpacing: '0.01em',
                  marginBottom: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                  flexWrap: 'wrap',
                }}>
                  {imageDimensions && (
                    <span>{imageDimensions.w}×{imageDimensions.h}</span>
                  )}
                  {imageDimensions && detailsMegapixels !== null && detailsMegapixels > 0 && (
                    <>
                      <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.2)' }}>│</span>
                      <span>{detailsMegapixels}MP</span>
                    </>
                  )}
                  {detailsFileSize && (imageDimensions || (detailsMegapixels !== null && detailsMegapixels > 0)) && (
                    <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.2)' }}>│</span>
                  )}
                  {detailsFileSize && (
                    <span>{formatFileSize(detailsFileSize)}</span>
                  )}
                </div>
              )}

              {/* Tags row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingBottom: 4 }}>
                {detailsTags.length > 0 ? (
                  detailsTags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '6px 14px',
                        borderRadius: 20,
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.22)',
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: 13,
                        fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                        letterSpacing: '0.01em',
                      }}
                    >
                      <Hash size={12} strokeWidth={2} />
                      {tag}
                    </span>
                  ))
                ) : null}
                {/* "Add tag" pill */}
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 14px',
                  borderRadius: 20,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.22)',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: 13,
                  fontFamily: "'SamsungOne', 'Samsung Sans', 'Roboto', 'Inter', system-ui, sans-serif",
                  letterSpacing: '0.01em',
                  cursor: 'pointer',
                }}>
                  <Hash size={12} strokeWidth={2} />
                  Add tag
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── end Image Details Panel ───────────────────────────────────────── */}

      {/* SKR token burst — celebratory animation when a purchase completes */}
      <AnimatePresence>
        {purchaseBurst && (
          <motion.div
            key={`skr-burst-${purchaseBurst.id}`}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 2500,
            }}
          >
            {burstCoins.map(({ dx, dy, delay, size, rotate }, i) => (
              <motion.div
                key={i}
                initial={{
                  x: purchaseBurst.x,
                  y: purchaseBurst.y,
                  scale: 0.4,
                  opacity: 0,
                  rotate: 0,
                }}
                animate={{
                  x: purchaseBurst.x + dx,
                  y: purchaseBurst.y + dy,
                  scale: [0.4, 1.1, 0.9],
                  opacity: [0, 1, 1, 0],
                  rotate,
                }}
                transition={{
                  duration: 6.1,
                  delay,
                  ease: [0.16, 1, 0.3, 1],
                  times: [0, 0.05, 0.85, 1],
                }}
                style={{
                  position: 'absolute',
                  left: -size / 2,
                  top: -size / 2,
                  width: size,
                  height: size,
                  willChange: 'transform, opacity',
                }}
              >
                <SkrTokenIcon size={size} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screenshot / focus protection overlay — only for purchased wallpapers */}
      <AnimatePresence>
        {isPurchased && showProtectionOverlay && (
          <motion.div
            key="protection-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              position: 'absolute', inset: 0,
              zIndex: 2000,
              background: 'rgba(0,0,0,0.97)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              pointerEvents: 'none',
            }}
          >
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
              padding: '32px 40px',
              borderRadius: 20,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px)',
            }}>
              <div style={{
                width: 56, height: 56,
                borderRadius: '50%',
                background: 'rgba(139,92,246,0.15)',
                border: '1px solid rgba(139,92,246,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Eye size={24} color='rgba(167,139,250,0.9)' />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.9)',
                  fontFamily: "'Syne', sans-serif",
                  letterSpacing: '0.01em',
                  marginBottom: 6,
                }}>
                  Content Protected
                </div>
                <div style={{
                  fontSize: 12, color: 'rgba(255,255,255,0.4)',
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: '0.03em',
                }}>
                  Return to view your wallpaper
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Edit dialog (uploader only) ─────────────────────────────────── */}
      <AnimatePresence>
        {editOpen && (
          <motion.div
            key="edit-dialog-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'absolute', inset: 0, zIndex: 40,
              background: 'rgba(0,0,0,0.72)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20,
            }}
            onClick={() => !editSaving && setEditOpen(false)}
          >
            <motion.div
              key="edit-dialog"
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 360,
                background: 'rgba(12,10,24,0.98)',
                borderRadius: 20,
                border: '1px solid rgba(167,139,250,0.22)',
                padding: '22px 22px 20px',
                display: 'flex', flexDirection: 'column', gap: 16,
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                  fontSize: 15, fontWeight: 700, color: '#a78bfa',
                  fontFamily: "'Syne', sans-serif", letterSpacing: '-0.01em',
                }}>
                  Edit
                </span>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => !editSaving && setEditOpen(false)}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'rgba(240,240,245,0.55)',
                  }}
                >
                  <X size={14} />
                </motion.button>
              </div>

              {/* Title field */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{
                  fontSize: 10, fontWeight: 700, color: 'rgba(167,139,250,0.65)',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontFamily: "'Syne', sans-serif",
                }}>
                  Title
                </label>
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  maxLength={100}
                  placeholder="Wallpaper title"
                  disabled={editSaving}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box' as const,
                    fontFamily: "'Syne', sans-serif",
                    opacity: editSaving ? 0.6 : 1,
                    transition: 'border-color 0.14s',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>

              {/* Price field */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{
                  fontSize: 10, fontWeight: 700, color: 'rgba(167,139,250,0.65)',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontFamily: "'Syne', sans-serif",
                }}>
                  Price (SKR)
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={editPrice}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    setEditPrice(isNaN(v) ? 1 : v);
                  }}
                  disabled={editSaving}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box' as const,
                    fontFamily: "'DM Mono', monospace", fontWeight: 700,
                    opacity: editSaving ? 0.6 : 1,
                    transition: 'border-color 0.14s',
                    WebkitAppearance: 'none',
                  } as React.CSSProperties}
                  onFocus={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>

              {/* Save button */}
              <motion.button
                whileHover={editSaving ? {} : { scale: 1.02, boxShadow: '0 0 18px rgba(139,92,246,0.4)' }}
                whileTap={editSaving ? {} : { scale: 0.97 }}
                onClick={handleSaveEdit}
                disabled={editSaving}
                style={{
                  padding: '11px 16px', borderRadius: 12,
                  background: editSaving ? 'rgba(139,92,246,0.4)' : 'rgba(139,92,246,0.85)',
                  border: '1px solid rgba(167,139,250,0.45)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: editSaving ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: editSaving ? 0.75 : 1,
                  transition: 'all 0.15s ease',
                }}
              >
                {editSaving
                  ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
                  : 'Save Changes'
                }
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── end Edit dialog ───────────────────────────────────────────────── */}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ringtoneViewerGlow {
          0% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes ringtoneRing {
          0% { transform: scale(0.92); opacity: 0.4; }
          50% { transform: scale(1.0); opacity: 0.8; }
          100% { transform: scale(0.92); opacity: 0.4; }
        }
        @keyframes waveBarV1 { 0% { transform: scaleY(0.5); } 100% { transform: scaleY(1); } }
        @keyframes waveBarV2 { 0% { transform: scaleY(0.7); } 100% { transform: scaleY(0.4); } }
        @keyframes waveBarV3 { 0% { transform: scaleY(1); } 100% { transform: scaleY(0.6); } }
        @keyframes waveBarV4 { 0% { transform: scaleY(0.4); } 100% { transform: scaleY(0.9); } }
      `}</style>
    </motion.div>,
    document.body
  );
};
